import { View, Text, Pressable } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  FileText,
  FolderOpen,
  ArrowRight,
  Layers,
} from 'lucide-react-native';
import Animated, { FadeInDown, FadeInUp } from 'react-native-reanimated';
import { useSystemThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { OnboardingLayout, STARTUP_ONBOARDING_DISPLAY_STEPS } from '@/components/OnboardingLayout';
import { OnboardingSignOut } from '@/components/OnboardingSignOut';
import AnimatedPressable from '@/components/AnimatedPressable';
import { InterviewOnboardingCard } from '@/components/interview/InterviewOnboardingCard';
import { track } from '@/lib/analytics';
import { useTrackScreen } from '@/lib/analytics';
import { useAuth } from '@/lib/supabase';
import { persistOnboardingStep } from '@/lib/onboarding/persist-step';
import { deferOnboarding } from '@/lib/onboarding/defer-onboarding';
import { useEntrance } from '@/lib/a11y/reduced-motion';

interface OptionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onPress: () => void;
  disabled?: boolean;
  badge?: string;
  colors: ReturnType<typeof useSystemThemeColors>;
  delay: number;
}

function OptionCard({
  icon,
  title,
  description,
  onPress,
  disabled,
  badge,
  colors,
  delay,
}: OptionCardProps) {
  const entrance = useEntrance();
  return (
    <Animated.View entering={entrance(FadeInUp.delay(delay).duration(500))}>
      <AnimatedPressable
        onPress={onPress}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={badge ? `${title}. ${badge}. ${description}` : `${title}. ${description}`}
        accessibilityState={{ disabled: !!disabled }}
        style={{
          minHeight: 44,
          backgroundColor: colors.surface.primary,
          borderRadius: 16,
          padding: 20,
          borderWidth: 1,
          borderColor: disabled ? colors.border.light : colors.border.medium,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <View className="flex-row items-start gap-4">
          <View
            className="w-12 h-12 rounded-xl items-center justify-center"
            style={{
              backgroundColor: disabled ? colors.surface.tertiary : colors.primary[50],
            }}
          >
            {icon}
          </View>
          <View className="flex-1">
            <View className="flex-row items-center gap-2 mb-1">
              <Text
                style={{ color: disabled ? colors.text.tertiary : colors.text.primary }}
                className="text-base font-bold"
              >
                {title}
              </Text>
              {badge ? (
                <View
                  className="px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: colors.secondary[50] }}
                >
                  <Text
                    style={{ color: colors.secondary[600] }}
                    className="text-xs font-semibold"
                  >
                    {badge}
                  </Text>
                </View>
              ) : null}
            </View>
            <Text
              style={{ color: disabled ? colors.text.tertiary : colors.text.secondary }}
              className="text-sm leading-relaxed"
            >
              {description}
            </Text>
          </View>
          {!disabled && <ArrowRight size={20} color={colors.text.tertiary} />}
        </View>
      </AnimatedPressable>
    </Animated.View>
  );
}

export default function DataInputChoiceScreen() {
  useTrackScreen('DataInputChoice');
  const router = useRouter();
  const colors = useSystemThemeColors();
  const { isDesktop } = useResponsive();
  const { user, refreshProfile } = useAuth();
  const entrance = useEntrance();

  const handlePitchDeck = async () => {
    track('onboarding_data_choice', { choice: 'pitch-deck' });
    if (user?.id) await persistOnboardingStep(user.id, 'data-source');
    // Activation §8 — data source chosen, after the awaited resume-pointer write.
    track('data_source_chosen', { source: 'pitch-deck' });
    router.push('/(onboarding)/pitch-deck-upload');
  };

  const handleDataRoom = async () => {
    track('onboarding_data_choice', { choice: 'data-room' });
    if (user?.id) await persistOnboardingStep(user.id, 'data-source');
    track('data_source_chosen', { source: 'data-room' });
    router.push('/(onboarding)/data-room-connect');
  };

  const handleInterview = async () => {
    track('onboarding_data_choice', { choice: 'interview' });
    // The interview lives INSIDE the app shell — persist the resume pointer so
    // relaunch returns here, AND set the durable defer so `shouldEnterApp` lets
    // the user back in mid-interview instead of bouncing them into onboarding
    // (the app-entry gate is completed OR deferred; a first score also flips it
    // complete later). Both were missing → cold-launch bounce.
    if (user?.id) {
      await persistOnboardingStep(user.id, 'data-source');
      await deferOnboarding(user.id, 'DataInputChoice.handleInterview');
    }
    track('data_source_chosen', { source: 'interview' });
    router.push('/(startup)/interview');
  };

  const handleSkip = async () => {
    track('onboarding_data_choice', { choice: 'skip' });
    // "Do it later" must be a DURABLE defer: persist the resume pointer AND set
    // `onboarding_deferred=true` so the app-entry gate (completed OR deferred)
    // lets the user in — without it, a cold launch bounced them back into
    // onboarding. The resume banner still nudges them to finish.
    if (user?.id) {
      await persistOnboardingStep(user.id, 'data-source');
      await deferOnboarding(user.id, 'DataInputChoice.handleSkip');
    }
    // Activation §8 — no data source now: this is the `deferred` outcome.
    track('data_source_chosen', { source: 'deferred' });
    await refreshProfile();
    router.replace('/(startup)/startup-dashboard');
  };

  const optionsContent = (
    <View className="gap-4">
      {/* Have a quick chat — recommended, low-friction path (no data room needed) */}
      <InterviewOnboardingCard
        colors={colors}
        onPress={handleInterview}
        delay={200}
      />

      {/* Upload Pitch Deck */}
      <OptionCard
        icon={<FileText size={24} color={colors.primary[600]} />}
        title="Upload Pitch Deck"
        description="Upload your pitch deck and we'll extract key data automatically"
        onPress={handlePitchDeck}
        colors={colors}
        delay={300}
      />

      {/* Connect Data Room */}
      <OptionCard
        icon={<FolderOpen size={24} color={colors.primary[600]} />}
        title="Connect Data Room"
        description="Link your Google Drive or Dropbox for comprehensive analysis"
        onPress={handleDataRoom}
        colors={colors}
        delay={400}
      />

      {/* Skip for now */}
      <Animated.View entering={entrance(FadeInUp.delay(500).duration(500))} className="mt-2">
        <Pressable
          onPress={handleSkip}
          accessibilityRole="button"
          accessibilityLabel="Skip for now — explore the platform first"
          className="py-3 items-center active:opacity-70"
          style={{ minHeight: 44, justifyContent: 'center' }}
        >
          <Text style={{ color: colors.text.secondary }} className="text-sm font-medium">
            Skip for now — explore the platform first
          </Text>
        </Pressable>
      </Animated.View>
    </View>
  );

  // Desktop layout with OnboardingLayout
  if (isWeb && isDesktop) {
    return (
      <OnboardingLayout
        currentStep="data-source"
        steps={STARTUP_ONBOARDING_DISPLAY_STEPS}
        title="How would you like to get scored?"
        subtitle="Choose how to share your startup data, or skip and explore first"
        userType="startup"
      >
        {optionsContent}
      </OnboardingLayout>
    );
  }

  // Mobile layout
  return (
    <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
      <SafeAreaView edges={['top', 'bottom']} className="flex-1">
        {/* Header */}
        <Animated.View
          entering={entrance(FadeInDown.delay(100).duration(500))}
          className="px-5 pt-4 pb-4"
        >
          <View className="flex-row items-center justify-between">
            <Pressable
              onPress={() => router.back()}
              accessibilityRole="button"
              accessibilityLabel="Go back"
              className="w-11 h-11 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.surface.primary }}
            >
              <ChevronLeft size={24} color={colors.text.primary} />
            </Pressable>
            <View className="flex-row gap-2">
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.primary[500] }}
              />
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.primary[500] }}
              />
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.primary[500] }}
              />
              <View
                className="h-1 w-8 rounded-full"
                style={{ backgroundColor: colors.border.light }}
              />
            </View>
            <OnboardingSignOut />
          </View>
        </Animated.View>

        {/* Title */}
        <Animated.View
          entering={entrance(FadeInDown.delay(150).duration(500))}
          className="px-5 pb-6"
        >
          <View className="flex-row items-center gap-3 mb-2">
            <Layers size={24} color={colors.primary[600]} />
            <Text style={{ color: colors.text.tertiary }} className="text-sm font-medium">
              Step 3 of 4
            </Text>
          </View>
          <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">
            How would you like to get scored?
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-base mt-1">
            Choose how to share your data, or skip and explore first
          </Text>
        </Animated.View>

        {/* Options */}
        <View className="flex-1 px-5 justify-center pb-8">
          {optionsContent}
        </View>
      </SafeAreaView>
    </View>
  );
}
